import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { MaybeUndef } from 'types/UtilityTypes';
import { TMS, TMSTimezoneConfig } from 'types/enums/Integrations';
import captureException from 'utils/captureException';
import { normalizeTimezone } from 'utils/normalizeTimezone';
import { isLikelyPhoneNumber } from 'utils/phoneAndDateParser';

dayjs.extend(utc);
dayjs.extend(timezone);

const SUGGESTION_DATETIME_DISPLAY_FORMAT = 'YYYY-MM-DD HH:mm';

/**
 * Stop timezone for flattened load-building fields (e.g. pickup.apptStartTime -> pickup.timezone).
 * Matches {@link LoadDateTimeInput} section resolution.
 */
export function getStopTimezoneForFlattenedField(
  fieldName: string,
  flattenedChanges: Record<string, unknown>
): MaybeUndef<string> {
  const section = fieldName.split('.')[0];
  if (!section) {
    return undefined;
  }

  const prefixes =
    section === 'consignee' ? ['consignee', 'dropoff'] : [section];

  for (const prefix of prefixes) {
    const tz = flattenedChanges[`${prefix}.timezone`];
    if (typeof tz === 'string' && tz.trim() !== '') {
      return tz;
    }
  }

  return undefined;
}

/**
 * Format load-building datetimes for Kit preview (and related UI) without applying the
 * user's browser timezone. Mirrors {@link LoadDateTimeInput}: TZ-agnostic TMSes show UTC
 * wall clock; others use the stop's IANA timezone when available.
 */
export function formatLoadDateTimeForDisplay(
  value: unknown,
  tmsName: TMS,
  fieldName?: string,
  flattenedChanges?: Record<string, unknown>
): string {
  return formatLoadInstantForDisplay(value, {
    tmsName,
    fieldName,
    flattenedChanges,
    format: SUGGESTION_DATETIME_DISPLAY_FORMAT,
  });
}

/**
 * Format a load/appointment instant for Kit UI without the browser timezone.
 * TZ-agnostic TMSes (or missing TMS) → UTC wall clock.
 * TZ-aware TMSes → stop IANA timezone when available, otherwise UTC.
 */
export function formatLoadInstantForDisplay(
  value: unknown,
  opts: {
    tmsName?: MaybeUndef<string>;
    stopTimezone?: MaybeUndef<string>;
    fieldName?: string;
    flattenedChanges?: Record<string, unknown>;
    format: string;
  }
): string {
  const isDateLike =
    value &&
    (value instanceof Date ||
      (typeof value === 'string' &&
        value.length > 8 &&
        !isLikelyPhoneNumber(value))) &&
    dayjs(value).isValid();

  if (!isDateLike) {
    return String(value ?? '');
  }

  const tmsName = opts.tmsName;
  const knownTms =
    tmsName && tmsName in TMSTimezoneConfig ? (tmsName as TMS) : undefined;
  const isTZAgnostic = knownTms
    ? (TMSTimezoneConfig[knownTms]?.isLoadTimezoneAgnostic ?? true)
    : true;

  if (isTZAgnostic) {
    return dayjs.utc(value).format(opts.format);
  }

  const rawTimezone =
    opts.stopTimezone ??
    (opts.fieldName && opts.flattenedChanges
      ? getStopTimezoneForFlattenedField(opts.fieldName, opts.flattenedChanges)
      : undefined);
  const effectiveTimezone = rawTimezone
    ? normalizeTimezone(rawTimezone)
    : undefined;

  if (effectiveTimezone) {
    return dayjs(value).tz(effectiveTimezone).format(opts.format);
  }

  return dayjs.utc(value).format(opts.format);
}

/**
 * Glossary:
 * - Normalize: Convert a date to UTC
 * - Denormalize: Convert a date (usually UTC) to the user's timezone
 */

export function normalizeUTCDate(date: Date) {
  const localOffset = date.getTimezoneOffset();
  return new Date(date.getTime() + localOffset * 60_000);
}

export function denormalizeUTCDate(date: Date) {
  const localOffset = date.getTimezoneOffset();
  return new Date(date.getTime() - localOffset * 60_000);
}

export function formatDateForTimeInput(date: Date) {
  if (!date) {
    return;
  }
  let hours = `${date.getHours()}`;
  let minutes = `${date.getMinutes()}`;

  if (hours.length < 2) {
    hours = `0${hours}`;
  }

  if (minutes.length < 2) {
    minutes = `0${minutes}`;
  }

  return `${hours}:${minutes}`;
}

/**
 * Converts timestamp to `targetTimezone` and formats using Dayjs. If no timezone provided, Dayjs defaults to user's locale.
 *
 * @example
 * const date = dayjs('2024-03-30T15:30:00Z');
 * const formattedStr = formatTimestamp(date, 'MM/DD HH:mm z', 'America/Los_Angeles'); // Output: '03/30 08:30 PDT'
 * const noTZParam = formatTimestamp(date, 'MM/DD HH:mm z'); // Output: '03/30 11:30 EDT' if user client's locale is America/New_York
 * const noTZFormatOrParam = formatTimestamp(date, 'MM/DD HH:mm'); // Output: '03/30 11:30' if user client's locale is America/New_York
 * const noTZInFormat = formatTimestamp(date, 'MM/DD HH:mm', 'America/Los_Angeles'); // Output: '03/30 08:30'
 *
 * @param {date} Dayjs - The timestamp to be formatted.
 * @param {format} string - Format using dayjs syntax. Docs: https://day.js.org/docs/en/display/format.
 * Most relevant to know is `'z'` adds the abbreviated named offset (aka EST/EDT, PST/PDT, etc)
 * @param {targetTimezone} string - The target IANA timezone, e.g. America/New_York to convert to.
 * If left empty, then `date` is formatted in user's locale (see examples above)
 */
export function formatTimestamp(
  date: Dayjs,
  format: string,
  targetTimezone?: string
): string {
  if (targetTimezone) {
    return date.tz(targetTimezone).format(format);
  }

  return date.format(format);
}

/**
 * Short timezone label for UI (e.g. "CDT") using the current date for DST.
 * Suppresses the label for TMSes that display load datetimes without location TZ (Aljex, Tai, QuantumEdge),
 * and when no stop/location timezone is set (UTC is used internally but not shown in labels).
 */
export function getShortTimezoneAbbreviation(
  ianaTimeZone: MaybeUndef<string>,
  tmsName: TMS
): string {
  if (
    TMSTimezoneConfig[tmsName]?.isLoadTimezoneAgnostic ||
    !ianaTimeZone ||
    ianaTimeZone === 'UTC'
  ) {
    return '';
  }
  try {
    const date = new Date();
    return (
      new Intl.DateTimeFormat('en-US', {
        timeZone: ianaTimeZone,
        timeZoneName: 'short',
      })
        .formatToParts(date)
        .find((part) => part.type === 'timeZoneName')?.value || ''
    );
  } catch (error) {
    captureException(error);
    return '';
  }
}
